import type { DriftAccount, DriftQueueFilter } from '@interfaces/driftQueue';
import type { FC } from 'react';
import { useState } from 'react';
import { DriftQueueVisual } from './driftQueue.visual';
import { useDriftQueue } from './useDriftQueue';

export type DriftQueueProps = {
  /** Override the page heading. Defaults to the `driftQueue:title` i18n key. */
  title?: string;
  /** Timestamp of the last data refresh. Defaults to mount time when not provided. */
  lastRefreshedAt?: Date;
  /**
   * Handler for the "View All" header button.
   * When `undefined` (default), the button is hidden until the destination is wired.
   */
  onViewAllClick?: () => void;
  /**
   * External source rows. When provided, replaces the mock-backed default so the
   * widget can be fed real data (e.g. the Portfolio Trading Hub page). Storybook
   * keeps its mock-backed default by omitting this prop.
   */
  accounts?: DriftAccount[];
  /**
   * Row-click handler for in-page deep-linking (e.g. Portfolio Trading Hub).
   * When omitted, row clicks are inert until an integrator wires navigation.
   */
  onAccountClick?: (account: DriftAccount) => void;
  /** When false, hides the "As of …" subtitle (dashboard shows it on Today's Snapshot). */
  showLastRefreshed?: boolean;
};

export const DriftQueue: FC<DriftQueueProps> = ({
  title,
  lastRefreshedAt,
  onViewAllClick,
  accounts,
  onAccountClick,
  showLastRefreshed = true,
}) => {
  const [activeFilter, setActiveFilter] =
    useState<DriftQueueFilter>('needsAction');
  const [internalLastRefreshed] = useState(() => new Date());

  const resolvedLastRefreshed = lastRefreshedAt ?? internalLastRefreshed;

  const { table, counts, isLoading, error, totalRowCount, filteredRowCount } =
    useDriftQueue({ activeFilter, accounts });

  const handleRowClick = (account: DriftAccount) => {
    onAccountClick?.(account);
  };

  return (
    <DriftQueueVisual
      table={table}
      counts={counts}
      activeFilter={activeFilter}
      onFilterChange={setActiveFilter}
      isLoading={isLoading}
      error={error}
      totalRowCount={totalRowCount}
      filteredRowCount={filteredRowCount}
      onRowClick={handleRowClick}
      title={title}
      lastRefreshedAt={resolvedLastRefreshed}
      onViewAllClick={onViewAllClick}
      showLastRefreshed={showLastRefreshed}
    />
  );
};

export default DriftQueue;
